"use client";!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();
import type { BlogPost } from "../../blog/types"
import React, { useState, Suspense, lazy, useRef } from "react"
import TopNavBar from "../../../components/common/TopNavBar"
import SnackbarMessage from "../../../components/common/SnackbarMessage"
import ProfileScoreCard from "../../../components/pages/group-leader/ProfileScoreCard"
import Story from "../../../components/pages/group-leader/Story"
import { usePathname } from "next/navigation"
import Loader from "../../../components/common/Loader"
import { AUTH_ROLE_ZEST_ADMIN } from "../../../lib/constants"
import useSessionAttributes from "../../../lib/auth/useSessionAttributes"
import useGetMe from "../../../hooks/useGetMe"
import useGetProvider from "../../../hooks/useGetProvider"
import { useQuery } from "@apollo/client/react"
import publicProviderMessageable from "../../../graphql/queries/publicProviderMessageable"
const ActiveGroups = lazy(
  () => import("../../../components/pages/group-leader/ActiveGroups"),
)
const PrimaryModalities = lazy(
  () => import("../../../components/pages/group-leader/PrimaryModalities"),
)
const Personality = lazy(
  () => import("../../../components/pages/group-leader/Personality"),
)
const Credentials = lazy(
  () => import("../../../components/pages/group-leader/Credentials"),
)
const LivedExperience = lazy(
  () => import("../../../components/pages/group-leader/LivedExperience"),
)
const RecentArticles = lazy(
  () => import("../../../components/pages/group-leader/RecentArticles"),
)
const PreviewAndPublishProfileBanner = lazy(
  () =>
    import("../../../components/pages/group-leader/PreviewAndPublishProfileBanner"),
)
const BottomButtons = lazy(
  () => import("../../../components/pages/group-leader/BottomButtons"),
)
const SocialLinksDisplay = lazy(
  () => import("../../../components/pages/group-leader/SocialLinks"),
)
const ReachOut = lazy(
  () => import("../../../components/pages/group-leader/ReachOut"),
)
const SharePageDialog = lazy(
  () => import("../../../components/common/SharePageDialog"),
)
const FooterSimple = lazy(() => import("../../../components/common/FooterSimple"))
const FORM_ID = process.env.NEXT_PUBLIC_HIPAATIZER_CONTACT_THERAPIST_FORM

type GroupLeaderPageProps = {
  provider: Record<string, unknown> | null
  user: Record<string, unknown> | null
  providerId: number
  /** Canonical site-relative path of this profile (vanity when minted). */
  providerPath?: string | null
  groups: Array<Record<string, unknown>>
  groupsTotalCount: number
  isPreviewableProfile: boolean
  blogPosts: BlogPost[]
  isAdmin: boolean
}

type PublicProviderMessageableData = {
  publicProviderMessageable?: {
    nodes?: Array<{ canMessage?: boolean }>
  }
}

export default function GroupLeaderPage({
  provider,
  user,
  providerId,
  providerPath,
  groups,
  groupsTotalCount,
  isPreviewableProfile,
  blogPosts,
  isAdmin,
}: GroupLeaderPageProps) {
  const pathname = usePathname()
  const pageUrl = `${process.env.NEXT_PUBLIC_APP_URL}${pathname}`
  const groupsSectionDivRef = useRef<HTMLDivElement | null>(null)
  const { userId } = useSessionAttributes()
  const [{ me }] = useGetMe()
  const [{ profileComplete, profilePendingPublication, onboardingStatus }] =
    useGetProvider()

  // Query for provider messaging capability
  const { data: publicProviderMessageableData } =
    useQuery<PublicProviderMessageableData>(publicProviderMessageable, {
      variables: { iProviderId: providerId },
      skip: !providerId,
    })
  const isProviderMessageable =
    publicProviderMessageableData?.publicProviderMessageable?.nodes?.[0]?.canMessage

  const [showSharePageDialog, setShowSharePageDialog] = useState(false)

  const providerObj = provider
  const userObj = user

  const handleScrollToGroups = () => {
    if (!groupsSectionDivRef.current) {
      return
    }
    const top =
      groupsSectionDivRef.current.getBoundingClientRect().top + window.scrollY - 100 // 50 is the offset you want to scroll above the element
    window.scrollTo({ top: top, behavior: "smooth" })
  }

  const renderPreviewAndPublishProfileBanner = () => {
    if (isAdmin) {
      // we need to add published flag to endpoint.
      // if (isPreviewableProfile) {
      //   return (
      //     <div className="bg-gray h-20px justify-center items-center flex w-full">
      //       Not published
      //     </div>
      //   )
      // }
      return null
    }

    return isPreviewableProfile ? (
      <Suspense fallback={<Loader />} key={"publish-banner"}>
        <PreviewAndPublishProfileBanner
          providerId={providerId}
          userId={userId}
          provider={providerObj}
          me={me}
          onboardingStatus={onboardingStatus}
          profileComplete={profileComplete}
          profilePendingPublication={profilePendingPublication}
        />
      </Suspense>
    ) : null
  }
  const renderSharePageDialog = () => {
    return (
      <Suspense fallback={<div>Loading...</div>} key={"share-group"}>
        <SharePageDialog
          open={showSharePageDialog}
          onClose={() => {
            setShowSharePageDialog(false)
          }}
          url={pageUrl}
          title="Share This Profile!"
        />
      </Suspense>
    )
  }
  return (
    <div>
      <TopNavBar startingBackgroundSolid={true} />
      {renderPreviewAndPublishProfileBanner()}
      <ProfileScoreCard provider={providerObj} user={userObj} />
      <Story provider={providerObj} />
      <Suspense fallback={null} key={"social-links"}>
        <SocialLinksDisplay provider={providerObj} />
      </Suspense>
      <Suspense fallback={<div>Loading...</div>} key={"groups"}>
        <ActiveGroups
          groups={groups}
          providerId={providerId}
          providerSlug={(provider?.publicId as string) || providerId}
          providerPath={providerPath}
          groupsSectionDivRef={groupsSectionDivRef}
          groupsTotalCount={groupsTotalCount}
          isPreviewableProfile={isPreviewableProfile}
        />
      </Suspense>
      <Suspense fallback={<div>Loading...</div>} key={"creds"}>
        <Credentials provider={providerObj} />
      </Suspense>
      <Suspense fallback={<div>Loading...</div>} key={"mods"}>
        <PrimaryModalities provider={providerObj} />
      </Suspense>
      <Suspense fallback={<div>Loading...</div>} key={"pers"}>
        <Personality provider={providerObj} />
      </Suspense>
      <Suspense fallback={<div>Loading...</div>} key={"lived exp"}>
        <LivedExperience provider={providerObj} />
      </Suspense>
      <Suspense fallback={<div>Loading...</div>} key={"recent-articles"}>
        <RecentArticles articles={blogPosts} />
      </Suspense>
      <Suspense fallback={<div>Loading...</div>} key={"bottom-buttons"}>
        <BottomButtons
          setShowShareProfileDialog={setShowSharePageDialog}
          joinGroupsOnClick={handleScrollToGroups}
        />
      </Suspense>
      <Suspense fallback={<div>Loading...</div>} key={"reach-out"}>
        <ReachOut
          providerObj={providerObj}
          user={userObj}
          isLoggedIn={!!userId}
          formId={FORM_ID}
          publicProviderMessageable={isProviderMessageable}
        />
      </Suspense>
      <div className={"h-[100px]"} />
      <Suspense fallback={<div>Loading...</div>} key={`footer`}>
        <FooterSimple />
      </Suspense>
      {showSharePageDialog ? renderSharePageDialog() : null}
      <SnackbarMessage />
    </div>
  )
}
